import { canAccessState } from "@/store/access";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useRouter } from "next/router";
import { effectivePermissionsState } from "@/store/permissions";
import { hasPermission } from "@/utils/session-permissions";
import { useCustomer } from "./use-customer";
import { getUserToken } from "@/services/storage-data";

const useGomakeAuth = (permissionEnumValue, allowAnonymous?: boolean) => {
  const { user, validate, isLanguageUpdated, getPrintHouseSetupSteps } =
    useCustomer(permissionEnumValue, allowAnonymous);

  const [, setCanAccessvalue] = useRecoilState(canAccessState);
  const [isAuth, setIsAuth] = useState<boolean | null>(null);

  const check = useCallback(async () => {
    if (allowAnonymous) {
      if (!!getUserToken()) {
        await validate();
      }

      setCanAccessvalue(true);
      setIsAuth(true);
      return;
    }
    const canAccess = await validate();
    setCanAccessvalue(canAccess);
    setIsAuth(canAccess);

    await getPrintHouseSetupSteps();
  }, [setIsAuth]);

  useEffect(() => {
    check();
  }, []);

  // Changing the selected lines of business changes the effective permissions. If the
  // page the user is on is no longer allowed, send them home instead of leaving it open.
  const router = useRouter();
  const effectivePermissions = useRecoilValue(effectivePermissionsState);
  const wasAllowedRef = useRef(false);
  useEffect(() => {
    if (
      allowAnonymous ||
      isAuth !== true ||
      permissionEnumValue === null ||
      permissionEnumValue === undefined ||
      effectivePermissions.length === 0
    ) {
      return;
    }
    const allowed = hasPermission(effectivePermissions, permissionEnumValue);
    if (!allowed && wasAllowedRef.current) {
      wasAllowedRef.current = false;
      router.replace("/");
      return;
    }
    wasAllowedRef.current = allowed;
  }, [effectivePermissions, isAuth, permissionEnumValue, allowAnonymous, router]);

  return { user, isAuth, isLanguageUpdated };
};

export { useGomakeAuth };
